// The two invite blocks of the hub CLAN panel (player-reach.md §5.4, R1306).
// The CLAN panel draws LAST SEASON'S SQUAD under its own sub-header on the
// MEMBERS page and as the companion of a clanless account, and INVITES is
// a page of its own with no card round it (R1945).
//
//   - LAST SEASON'S SQUAD: the other humans of the account's last pickup
//     squad (`GET /api/pickup-squad`). A mate the account may invite now
//     carries INVITE. Every other mate carries one short line with the
//     reason the server gave, because the server applies the same rule to
//     the card and to the write.
//   - INVITES: the open clan invites of a clanless account
//     (`GET /api/invites`), each with ACCEPT and DECLINE. DECLINE ends the
//     invite and takes the second press of the armed-control rule (R837).
//     ACCEPT takes one press, the same as APPLY to an `open` clan: the
//     client arms no clan join.
//
// A card with nothing to show draws nothing, so a player who sailed in no
// pickup squad and holds no invite sees the CLAN page as before.
//
// The cards own no call state of their own. The CLAN panel runs every
// write through its one `call`, which draws the busy line and the refusal,
// and `refusalFor` below gives the line of each invite refusal.

import { button } from '../../choice/choice';
import {
  CODE_ALREADY_INVITED, CODE_INVITE_CAP, CODE_INVITE_EXPIRED, CODE_INVITEE_IN_CLAN,
  CODE_NOT_PICKUP_MATE,
  acceptInvite, declineInvite, getInvites, getPickupSquad, inviteToClan,
  type ClanInvite, type InviteReason, type PickupMate,
} from '../../../net/api';
import { hubPickup as S } from '../../../gen/strings';
import type { ArmGate } from '../../arm';
import { TTL_ON_OPEN, formatSpan, secondsUntil } from '../hubstate';
import {
  armedButton, armedPress, esc, factionTag, failed, lede, ctlRow, plain, type PanelContext,
} from '../panel';

/** The cache keys of the two reads. A write drops both. */
const PICKUP_CELL = 'pickup-squad';
const INVITES_CELL = 'invites';

/** The line beside a mate the account may not invite now. The server sends
 *  the reason, so the table is a `Map`: a word that names a prototype
 *  member reads as unknown (R1341). */
const REASON_LINES: ReadonlyMap<string, string> = new Map([
  ['no_clan', S.reason_no_clan],
  ['no_right', S.reason_no_right],
  ['in_clan', S.reason_in_clan],
  ['invited', S.reason_invited],
  ['clan_full', S.reason_clan_full],
  ['invite_cap', S.reason_invite_cap],
]);

/** The reason line of one mate. A reason this build does not know draws no
 *  line and no INVITE: the panel offers no press it cannot explain. */
export function reasonLine(reason: InviteReason): string {
  return REASON_LINES.get(reason) ?? '';
}

/** The line of each refusal of an invite route, by the wire code. */
const REFUSALS: ReadonlyMap<string, string> = new Map([
  [CODE_NOT_PICKUP_MATE, S.refusal_not_pickup_mate],
  [CODE_INVITEE_IN_CLAN, S.refusal_invitee_in_clan],
  [CODE_ALREADY_INVITED, S.refusal_already_invited],
  [CODE_INVITE_CAP, S.refusal_invite_cap],
  [CODE_INVITE_EXPIRED, S.refusal_invite_expired],
]);

/** What the panel says about one refusal of an invite route, or '' for a
 *  code that is not one of them. */
export function inviteRefusalFor(code: string): string {
  return REFUSALS.get(code) ?? '';
}

/** How the CLAN panel runs one write: the busy label, the call, and what
 *  follows a success. It is the panel's own `call`. */
export type RunCall = <T>(label: string, fn: () => Promise<T>, done?: (out: T) => void) => void;

/** The two blocks, as the CLAN panel mounts them. */
export interface InviteCards {
  /** The content of LAST SEASON'S SQUAD with no card round it, or ''
   *  while there is nothing to show: a section of MEMBERS, and the
   *  companion column of a clanless account (ui-map-interface.md §16.11). */
  squadBody(): string;
  /** The body of the INVITES page, or '' while there is nothing to show. */
  invitesCard(): string;
  /** How many open invites the account holds, or undefined while the read
   *  has not answered. */
  inviteCount(): number | undefined;
  /** Takes one press. Answers false for an action that is not the cards'. */
  act(action: string, el: HTMLElement): boolean;
}

/** What the CLAN panel lends the cards. */
export interface InviteHooks {
  /** The panel's one write path. */
  run: RunCall;
  /** Sets the plain outcome line above the page. */
  notice(text: string): void;
  /** The account joined a clan: the panel opens the chat and drops the
   *  browse list, as after an APPLY to an `open` clan. */
  joined(): void;
}

export function createInviteCards(ctx: PanelContext, gate: ArmGate, hooks: InviteHooks): InviteCards {
  const { run } = hooks;
  /** A success moves both lists: an invite changes the card of the
   *  officer, and an accept ends every invite of the account. */
  function dropBoth(): void {
    ctx.data.drop(PICKUP_CELL);
    ctx.data.drop(INVITES_CELL);
  }

  /** One mate: INVITE, or the line of the reason, or nothing for a reason
   *  this build does not know (R2218). */
  function mateRow(m: PickupMate): string {
    const line = reasonLine(m.reason);
    const control = m.reason === ''
      ? ctlRow(button(S.invite, { act: 'pickup-invite', data: { id: m.id } }, { variant: 'primary' }))
      : line === '' ? '' : lede(line);
    return `<div class="row"><span class="k">${esc(m.name)}</span>`
      + `<span class="v">${esc(S.mate_season({ mode: m.mode.toUpperCase(), season: m.season }))}</span></div>`
      + control;
  }

  function inviteRow(v: ClanInvite): string {
    const left = secondsUntil(ctx.now(), v.expiresAt);
    const from = v.invitedBy === undefined || v.invitedBy === '' ? '' : S.invite_from({ name: v.invitedBy });
    const key = `decline:${v.id}`;
    return `<div class="row"><span class="k">${esc(S.invite_clan({ name: v.clanName, tag: v.clanTag }))}</span>`
      + `<span class="v gold">${esc(S.invite_left({ left: formatSpan(left) }))}</span></div>`
      + (from === '' ? '' : lede(from))
      + ctlRow(factionTag(v.clanFaction)
        + button(S.accept, { act: 'invite-accept', data: { invite: String(v.id) } }, { variant: 'primary' })
        + armedButton(gate, key, S.decline, S.decline_armed, {
          act: 'invite-decline', data: { invite: String(v.id) },
        }));
  }

  function squadBody(): string {
    const cell = ctx.data.cell<PickupMate[]>(PICKUP_CELL, TTL_ON_OPEN, () => getPickupSquad());
    if (cell.state === 'failed') return failed(cell.error);
    const mates = cell.value;
    if (mates === undefined || mates.length === 0) return '';
    return lede(S.squad_lede) + mates.map(mateRow).join('');
  }

  function invitesCell() {
    return ctx.data.cell<ClanInvite[]>(INVITES_CELL, TTL_ON_OPEN, () => getInvites());
  }

  return {
    squadBody,

    inviteCount(): number | undefined {
      return invitesCell().value?.length;
    },

    invitesCard(): string {
      const cell = invitesCell();
      if (cell.state === 'failed') return plain(failed(cell.error));
      const list = cell.value;
      if (list === undefined || list.length === 0) return '';
      return plain(lede(S.invites_lede) + list.map(inviteRow).join(''));
    },

    act(action: string, el: HTMLElement): boolean {
      const invite = Number.parseInt(el.dataset.invite ?? '0', 10);
      switch (action) {
        case 'pickup-invite': {
          const id = el.dataset.id ?? '';
          if (id !== '') {
            run(S.busy_invite, () => inviteToClan(id), () => {
              dropBoth();
              hooks.notice(S.notice_invited);
            });
          }
          return true;
        }
        case 'invite-accept':
          if (invite > 0) {
            run(S.busy_accept, () => acceptInvite(invite), () => {
              dropBoth();
              hooks.joined();
            });
          }
          return true;
        case 'invite-decline':
          if (invite <= 0 || !armedPress(ctx, gate, `decline:${invite}`)) return true;
          run(S.busy_decline, () => declineInvite(invite), dropBoth);
          return true;
        default:
          return false;
      }
    },
  };
}
